import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Film, Camera, NotebookPen, Search, SlidersHorizontal, RotateCcw } from "lucide-react";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import ExpeditionMap from "@/components/ExpeditionMap";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { ExpeditionStatusBadge } from "@/components/ExpeditionExtras";
import { Eyebrow, FiveWorldsSection, PropositionSection, FilmPipelineSection, InDevelopmentSection, ExpeditionsFAQSection } from "@/components/expeditions/ExpeditionsSections";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import {
  WORLDS, EXPEDITION_TYPES, OUTPUT_OPTIONS, MONTHS, YEAR_OPTIONS, STATUS_FILTERS,
  EXPEDITIONS_FAQ, journeyMonths, journeyTerrains, matchesStatus,
} from "@/lib/expeditions";
import { fmtJourneyDate } from "@/lib/content";
import { Link } from "react-router-dom";

const OUT_ICONS = { DOCUMENTARY: Film, FILM: Film, PHOTOGRAPHY: Camera, "FIELD JOURNAL": NotebookPen, STORY: NotebookPen };

export const ExpeditionCard = ({ j }) => {
  const applicationsOpen = j.expedition_status === "UPCOMING" && ["OPEN", "UPCOMING"].includes(j.status);
  const terrainLabel = useMemo(() => journeyTerrains(j).join(" · ") || "Expedition", [j]);
  const placeLabel = useMemo(() => [j.location, j.country].filter(Boolean).join(" · "), [j]);
  const dateLabel = useMemo(() => [
    j.start_date ? `${fmtJourneyDate(j.start_date)}${j.end_date ? ` to ${fmtJourneyDate(j.end_date)}` : ""}` : "",
    j.duration_days ? `${j.duration_days} days` : "",
  ].filter(Boolean).join(" · "), [j]);
  return (
    <Link to={`/expeditions/${j.id}`} data-testid={`expedition-card-${j.id}`} className="group block bg-white border border-ptf-primary/12 hover:border-ptf-primary/40 transition-colors overflow-hidden h-full">
      {j.images?.[0] && (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={j.images[0]} alt={j.title} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">{terrainLabel}{j.region_group ? ` · ${j.region_group}` : ""}</p>
          <ExpeditionStatusBadge status={j.expedition_status} />
        </div>
        <h3 className="mt-2 font-display uppercase text-2xl tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{j.title}</h3>
        <p className="mt-1 text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/55">
          {placeLabel}
        </p>
        <p className="mt-0.5 text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/55">
          {dateLabel}
        </p>
        {(j.mission || j.summary) && <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed line-clamp-2">{j.mission || j.summary}</p>}
        {j.outputs?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {j.outputs.map((o) => {
              const I = OUT_ICONS[o] || NotebookPen;
              return <span key={o} className="inline-flex items-center gap-1 text-[0.56rem] uppercase tracking-[0.12em] bg-ptf-primary/5 text-ptf-primary px-2 py-1"><I className="h-3 w-3" />{o}</span>;
            })}
          </div>
        )}
        <span className="mt-4 flex items-center gap-4">
          <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary group-hover:text-ptf-secondary transition-colors">
            View Expedition <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
          {applicationsOpen && (
            <span data-testid={`apply-hint-${j.id}`} className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-emerald-700">Apply to Join</span>
          )}
        </span>
      </div>
    </Link>
  );
};

const EMPTY_FILTERS = { q: "", terrain: "", scope: "", month: "", year: "", status: "", output: "", type: "" };

const selCls = "w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-[0.68rem] uppercase tracking-[0.1em] outline-none focus:border-ptf-secondary transition-colors text-ptf-text";

function FilterSelects({ flt, set, idPrefix }) {
  const fields = [
    { key: "terrain", label: "What do you want to experience?", options: [["", "All"], ...WORLDS.map((w) => [w.key, w.name])] },
    { key: "scope", label: "Where?", options: [["", "India & World"], ["INDIA", "India"], ["WORLD", "World"]] },
    { key: "month", label: "Month", options: [["", "Any Month"], ...MONTHS.map((m, i) => [String(i + 1), m])] },
    { key: "year", label: "Year", options: YEAR_OPTIONS },
    { key: "status", label: "Status", options: STATUS_FILTERS },
    { key: "output", label: "Output", options: [["", "All"], ...OUTPUT_OPTIONS.map((o) => [o, o.charAt(0) + o.slice(1).toLowerCase()])] },
    { key: "type", label: "Format", options: [["", "All"], ...EXPEDITION_TYPES.map((t) => [t, t.split(" ").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")])] },
  ];
  return (
    <>
      {fields.map((f) => (
        <label key={f.key} className="flex flex-col justify-end h-full">
          <span className="block text-[0.58rem] uppercase tracking-[0.16em] text-ptf-text/55 mb-1.5">{f.label}</span>
          <select data-testid={`${idPrefix}-filter-${f.key}`} value={flt[f.key]} onChange={(e) => set(f.key, e.target.value)} className={selCls}>
            {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      ))}
    </>
  );
}

export default function Expeditions() {
  const { data: journeys = [] } = useQuery({ queryKey: ["journeys"], queryFn: async () => (await api.get("/journeys")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [flt, setFlt] = useState(EMPTY_FILTERS);
  const set = (k, v) => setFlt((p) => ({ ...p, [k]: v }));
  const reset = () => setFlt(EMPTY_FILTERS);
  const activeCount = Object.entries(flt).filter(([k, v]) => v && k !== "q").length;

  const filtered = useMemo(() => journeys.filter((j) => {
    if (flt.scope === "INDIA" && j.country !== "India") return false;
    if (flt.scope === "WORLD" && j.country === "India") return false;
    if (flt.terrain && !journeyTerrains(j).includes(flt.terrain)) return false;
    if (flt.month || flt.year) {
      const ms = journeyMonths(j);
      if (flt.month && !ms.some((x) => x.month === +flt.month && (!flt.year || flt.year === "FUTURE" || x.year === +flt.year))) return false;
      if (flt.year === "FUTURE") {
        const dev = ["CONCEPT", "RESEARCH", "PLANNING"].includes(j.expedition_status);
        if (!(ms.some((x) => x.year > 2028) || (!ms.length && dev))) return false;
      } else if (flt.year && !flt.month && !ms.some((x) => x.year === +flt.year)) return false;
    }
    if (flt.status && !matchesStatus(j, flt.status)) return false;
    if (flt.output && !(j.outputs || []).map((o) => String(o).toUpperCase()).includes(flt.output)) return false;
    if (flt.type) {
      const t = j.expedition_type || (j.format === "EXPEDITION" ? "EXPEDITION" : "");
      if (t !== flt.type) return false;
    }
    if (flt.q) {
      const hay = [j.title, j.location, j.country, j.region, j.region_group, j.description, j.mission, j.summary, j.expedition_type,
        ...journeyTerrains(j), ...(j.activities || []), ...(j.outputs || []), ...(j.keywords || []), ...(j.categories || [])]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(flt.q.toLowerCase().trim())) return false;
    }
    return true;
  }), [journeys, flt]);

  const counts = useMemo(() => journeys.reduce((a, j) => { journeyTerrains(j).forEach((t) => { a[t] = (a[t] || 0) + 1; }); return a; }, {}), [journeys]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const pickTerrain = (key, has) => {
    if (has) { set("terrain", flt.terrain === key ? "" : key); scrollTo("results"); }
    else scrollTo("in-development");
  };

  const jsonLd = [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: EXPEDITIONS_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }];

  return (
    <>
      <Seo title="Expeditions & Films" path="/expeditions" description="Search Planet Trotter expeditions by terrain, month and destination: wildlife expeditions, mountain expeditions, river, ocean and land journeys in India and around the world, documented through film, photography, stories and field journals." jsonLd={jsonLd} />

      <section data-testid="expeditions-hero" className="relative min-h-[85vh] flex items-end bg-ptf-primary text-ptf-bg grain overflow-hidden">
        <img src="/expeditions/river-dusk.jpg" alt="A field briefing by the river at dusk, from Planet Trotter expedition footage" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/55 to-ptf-primary/15" />
        <div className="relative mx-auto max-w-[1600px] w-full px-5 sm:px-8 lg:px-12 pb-14 md:pb-20 pt-40">
          <Eyebrow light>Planet Trotter Foundation</Eyebrow>
          <MaskText tag="h1" lines={["Expeditions", "& Films."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight" />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-ptf-bg/80 leading-relaxed">
              Journeys into mountains, rivers, oceans, landscapes and wildlife,
              experienced in the field and brought back through film, photography and stories.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <PtfButton href="#find" variant="accent" data-testid="hero-explore-expeditions">Find Your Expedition</PtfButton>
              <PtfButton to="/films" variant="outlineLight" data-testid="hero-watch-films">Watch Our Films</PtfButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="find" data-testid="find-expedition" className="bg-ptf-bg py-16 md:py-24 border-b border-ptf-primary/10">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>Expedition search</Eyebrow></Reveal>
          <MaskText lines={["Find your", "expedition."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight text-ptf-primary" />
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-2xl text-ptf-text/70 leading-relaxed">
              Choose where you want to go, what kind of landscape you want to experience and when you want to travel.
            </p>
          </Reveal>

          <div className="mt-10 bg-white border border-ptf-primary/12 p-5 md:p-6">
            <div className="flex flex-col md:flex-row gap-3">
              <label className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ptf-text/40" />
                <input
                  data-testid="expedition-search-input"
                  value={flt.q}
                  onChange={(e) => set("q", e.target.value)}
                  placeholder="What are you looking for?"
                  aria-label="Search expeditions"
                  className="w-full border border-ptf-primary/20 bg-white pl-10 pr-4 py-3 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text"
                />
              </label>
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <button type="button" data-testid="mobile-filters-open" className="w-full inline-flex items-center justify-center gap-2 border border-ptf-primary/25 px-5 py-3 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary">
                      <SlidersHorizontal className="h-4 w-4" /> Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto bg-ptf-bg">
                    <SheetHeader><SheetTitle className="font-display uppercase tracking-tight text-ptf-primary text-left">Filter expeditions</SheetTitle></SheetHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pb-4">
                      <FilterSelects flt={flt} set={set} idPrefix="m" />
                      <button type="button" data-testid="m-reset-filters" onClick={reset} className="inline-flex items-center justify-center gap-2 border border-ptf-primary/25 px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/70">
                        <RotateCcw className="h-3.5 w-3.5" /> Reset all
                      </button>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              {activeCount > 0 && (
                <button type="button" data-testid="reset-filters" onClick={reset} className="hidden lg:inline-flex items-center gap-2 border border-ptf-primary/25 px-5 py-3 text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/70 hover:border-ptf-primary transition-colors shrink-0">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </button>
              )}
            </div>
            <div className="hidden lg:grid grid-cols-7 gap-3 mt-4" data-testid="desktop-filter-bar">
              <FilterSelects flt={flt} set={set} idPrefix="d" />
            </div>
          </div>

          <div className="mt-10" data-testid="month-picker">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/60 mb-3">When do you want to go?</p>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
              {MONTHS.map((m, i) => {
                const v = String(i + 1);
                const on = flt.month === v;
                return (
                  <button key={m} type="button" data-testid={`month-${m.toLowerCase()}`} onClick={() => { set("month", on ? "" : v); scrollTo("results"); }}
                    className={`shrink-0 text-[0.65rem] uppercase tracking-[0.14em] px-4 py-2 border transition-colors ${on ? "bg-ptf-secondary text-ptf-bg border-ptf-secondary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}>
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="results" data-testid="expedition-results" className="bg-ptf-bg py-16 md:py-24 scroll-mt-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <Reveal><Eyebrow>The expedition archive</Eyebrow></Reveal>
              <MaskText lines={["The journey becomes", "the story."]} className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary" />
            </div>
            <p data-testid="result-count" className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-ptf-secondary shrink-0">
              {filtered.length} Expedition{filtered.length === 1 ? "" : "s"} Found
            </p>
          </div>
          {filtered.length === 0 ? (
            <div data-testid="no-expeditions" className="py-14 text-center border border-dashed border-ptf-primary/20">
              <p className="font-display uppercase text-2xl tracking-tight text-ptf-primary">No expeditions match your search.</p>
              <p className="mt-2 text-sm text-ptf-text/60">Try another month, terrain or destination.</p>
              <div className="mt-6"><PtfButton onClick={reset} data-testid="empty-reset">Clear all filters</PtfButton></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="expedition-grid">
              {filtered.map((j) => <ExpeditionCard key={j.id} j={j} />)}
            </div>
          )}
        </div>
      </section>

      <section data-testid="expedition-map-section" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>The expedition map</Eyebrow></Reveal>
          <MaskText lines={["Where we go."]} className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight text-ptf-primary" />
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-2xl text-ptf-text/70 leading-relaxed mb-10">
              Explore the places where Planet Trotter expeditions take us. The map follows your
              search: filter by terrain, destination or month and the markers follow.
            </p>
          </Reveal>
          <ExpeditionMap expeditions={filtered} scope={flt.scope} />
        </div>
      </section>

      <FiveWorldsSection counts={counts} settings={settings} pickTerrain={pickTerrain} />

      <PropositionSection />

      <FilmPipelineSection />

      <InDevelopmentSection />

      <ExpeditionsFAQSection />

      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">Go further.<br />Come back with a story.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/flagship" variant="accent" data-testid="final-apply-cta">Apply for an Expedition</PtfButton>
            <PtfButton to="/films" variant="outlineLight">Watch Our Films</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
